import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XIcon } from '@ngg/icons';
import { Carousel as NGGCarousel } from '@ngg/components';
import Image from 'next/image';
import { Image as TypeImage } from '@ts/image';
import PrevButton from '../carouselButtons/PrevButton';
import NextButton from '../carouselButtons/NextButton';
import Thumbnails from '@ui/components/PictureView/components/Thumbnails';

type Props = {
  open: boolean;
  active: number;
  onClose: () => void;
  images: TypeImage[];
  singleImage: Boolean;
  goToCarouselItem: (index: number, smooth?: boolean) => void;
};

const visibleSlides = 1;

export default function Fullscreen({
  open,
  active,
  onClose,
  images,
  singleImage,
  goToCarouselItem,
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const initialFocus = useRef<HTMLElement>(null);
  const [activeIndex, setActiveIndex] = useState(active);
  const observer = useRef<IntersectionObserver>(
    typeof window !== 'undefined'
      ? new IntersectionObserver(
          ([entry]) => {
            const target = entry.target as HTMLElement;
            if (entry.intersectionRatio > 0.5) {
              setActiveIndex(Number(target.dataset.index));
            }
          },
          {
            root: ref?.current,
            rootMargin: '0px',
            threshold: [0.5],
          },
        )
      : null,
  );

  const onMount = () => {
    if (!ref.current) return;

    Array.from(ref.current.children).forEach((child) => {
      observer.current?.observe(child);
    });
  };

  const nextButtonVisibility = useCallback(() => {
    if (!ref.current) return true;
    return activeIndex < ref.current.children.length - visibleSlides;
  }, [ref, activeIndex]);

  const goToItem = useCallback(
    (index: number, smooth = true) => {
      if (index < 0) return;
      if (ref.current && index === ref.current.children.length) return;

      const currentChild = ref.current?.children[index] as HTMLDivElement;
      ref?.current?.scrollTo({
        left: currentChild?.offsetLeft,
        behavior: smooth ? 'smooth' : 'auto',
      });
      setActiveIndex(index);
    },
    [ref, setActiveIndex],
  );

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const addEvent = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goToItem(activeIndex - 1, false);
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        goToItem(activeIndex + 1, false);
      }
    };
    window.addEventListener('keydown', addEvent);

    return () => window.removeEventListener('keydown', addEvent);
  }, [activeIndex, goToItem]);

  const Pagination = () => (
    <div className="relative mx-4 flex w-32 justify-center gap-2">
      <PrevButton
        isVisible={activeIndex > 0}
        onClick={(e) => {
          goToItem(activeIndex - 1, false);
          if (activeIndex - 1 <= 0) {
            e.currentTarget.blur();
          }
        }}
        classNames={{
          button: 'static h-6 translate-y-0 bg-transparent',
          disabled: 'opacity-10',
        }}
      />
      <div className="flex grow justify-center">
        {`${activeIndex + 1} / ${images.length}`}
      </div>
      <NextButton
        initialFocus={initialFocus}
        isVisible={nextButtonVisibility()}
        onClick={(e) => {
          goToItem(activeIndex + 1, false);
          if (
            ref?.current &&
            activeIndex + 1 >= ref.current.children.length - visibleSlides
          ) {
            e.currentTarget.blur();
          }
        }}
        classNames={{
          button: 'static h-6 translate-y-0 bg-transparent',
          disabled: 'opacity-10',
        }}
      />
    </div>
  );

  return (
    <Transition
      show={open}
      beforeEnter={() => goToItem(active, false)}
      afterEnter={() => onMount()}
      beforeLeave={() => goToCarouselItem(activeIndex, false)}
      afterLeave={() => observer.current?.disconnect()}>
      <Dialog
        initialFocus={initialFocus}
        onClose={onClose}
        className="fixed inset-0 z-50 bg-beige">
        <Dialog.Panel className="mx-auto flex h-full max-w-screen-2xl flex-col justify-between lg:block">
          <div className="absolute right-4 top-4 z-10 flex justify-end md:top-6">
           {!singleImage && (
             <div className="hidden lg:block">
               <Pagination />
             </div>
           )}
            <button
              type="button"
              onClick={onClose}
              className="action-focus grid h-6 w-6 place-content-center">
              <XIcon className="text-base md:text-xl" />
            </button>
          </div>
          {!singleImage && (
            <div className="hidden lg:block">
              <Thumbnails
                goToItem={goToItem}
                activeIndex={activeIndex}
                images={images}
                className="z-50 max-h-full max-w-full flex-shrink-0 py-2 md:bottom-0 md:top-0 md:flex md:overflow-auto lg:absolute lg:flex-col"
              />
            </div>
          )}

          <NGGCarousel
            ref={ref}
            classNames={{
              wrapper:
                '[--gap:1rem] pb-0 pt-16 md:px-32 md:pb-16 flex items-center h-full',
              slider: 'scrollbar-none md:h-full',
              item: 'flex items-center',
            }}>
            {images.map((media) => (
              <Fragment key={media.id}>
                {/* ONLY IMAGES FOR NOW, ADD SUPPORT FOR OTHER MEDIA TYPES LATER */}
                {media.type === 'image' ? (
                  <Image
                    src={media.thumbnail ?? media.src}
                    alt={media.alt}
                    width={1500}
                    height={1500}
                    className="mx-auto max-h-full max-w-full object-contain mix-blend-multiply"
                  />
                ) : null}
              </Fragment>
            ))}
          </NGGCarousel>

          {!singleImage && (
            <div className="mx-auto mb-4 flex justify-center lg:hidden">
              <Pagination />
            </div>
           )}
        </Dialog.Panel>
      </Dialog>
    </Transition>
  );
}
